import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { androidMenuFinishedLoading, iosMenuFinishedLoading } from 'flow/actions/embedded-app-actions';
import GeneralLink from 'flow/components/ui-components/GeneralLink';
import Icon from 'flow/components/ui-components/Icon';
import Loading from 'flow/components/ui-components/Loading';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import useNavigationMenu from 'flow/hooks/useNavigationMenu';
import useProfiles from 'flow/hooks/useProfiles';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import EmbeddedType from 'flow/types/embedded-type';
import { routes } from 'flow/types/routes';
import { isTeamsAnyVersion, isTeamsFullManualLogin } from 'flow/utils/app-utils';
import { getCallRecordingTitle } from 'flow/utils/call-recording-monitoring-utils';
import { getIntegrationHost } from 'flow/utils/is-integration';
import { hasVaryingNotifications } from 'flow/utils/notification-utils';
import { callerIdVaries, callRecordingVaries, channelLoginVaries, incomingCallsVaries, monitoringVaries, personalCallRecordingVaries, queueLoginVaries } from 'flow/utils/profile-utils';
import { generatePath } from 'flow/utils/router-utils';
import { twMerge } from 'tailwind-merge';
import BrandLogo from './BrandLogo';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var Nav = function Nav(_ref) {
  var label = _ref.label,
    icon = _ref.icon,
    path = _ref.path,
    showHelp = _ref.showHelp;
  var _useAppContext = useAppContext(),
    i18next = _useAppContext.i18next;
  var _useParams = useParams(),
    extensionKey = _useParams.extensionKey;
  if (!path) {
    return /*#__PURE__*/_jsx("div", {
      className: "ms-2 mt-6 mb-2 text-xs font-bold text-gray-800",
      children: i18next.t(label)
    });
  }
  return /*#__PURE__*/_jsx(NavLink, {
    "data-test-id": "settings-menu-".concat(icon),
    to: generatePath(path, {
      extensionKey: extensionKey
    }),
    className: "group block rounded-sm p-2 hover:bg-gray-200",
    activeClassName: "bg-gray-200 active",
    children: /*#__PURE__*/_jsxs("div", {
      className: "flex w-full items-center gap-x-3",
      children: [/*#__PURE__*/_jsx("div", {
        className: twMerge('flex size-10 items-center justify-center rounded-lg bg-gray-200', 'group-hover:bg-gray-300', 'group-[.active]:bg-gray-300'),
        children: /*#__PURE__*/_jsx(Icon, {
          iconKey: icon,
          size: "24",
          className: ['callerid', 'incoming'].includes(icon) && 'rtl:scale-x-[-1]'
        })
      }), /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx("div", {
          className: "leading-5",
          children: i18next.t(label)
        }), showHelp !== null && showHelp !== void 0 && showHelp() ? /*#__PURE__*/_jsx("p", {
          className: "text-sm text-gray-500",
          children: i18next.t('settings.varies_per_profile')
        }) : null]
      })]
    })
  }, label);
};
var SettingsNavigation = function SettingsNavigation() {
  var _useAppContext2 = useAppContext(),
    i18next = _useAppContext2.i18next,
    embedded = _useAppContext2.embedded;
  var dispatch = useAppDispatch();
  var _useParams2 = useParams(),
    extensionKey = _useParams2.extensionKey;
  var _useExtension = useExtension(extensionKey),
    extension = _useExtension.extension;
  var ownExtensionKey = useAppSelector(selectOwnExtensionKey);
  var _useNavigationMenu = useNavigationMenu(),
    config = _useNavigationMenu.config,
    adminPortalUrl = _useNavigationMenu.adminPortalUrl,
    goToStatsInsights = _useNavigationMenu.goToStatsInsights,
    logOut = _useNavigationMenu.logOut;
  var _useProfiles = useProfiles(extensionKey),
    profiles = _useProfiles.profiles;
  useEffect(function () {
    if (EmbeddedType.ANDROID.is(embedded)) {
      dispatch(androidMenuFinishedLoading());
    } else if (EmbeddedType.IOS.is(embedded)) {
      dispatch(iosMenuFinishedLoading());
    }
  }, [dispatch, embedded]);
  if (!extension) {
    return /*#__PURE__*/_jsx(Loading, {});
  }
  var getNavigationForSetting = function getNavigationForSetting(profiles, extension, config) {
    return [{
      label: 'Settings.Account',
      icon: 'account',
      path: routes['settings.account']
    }, {
      label: 'settings.general',
      icon: 'general',
      path: routes['settings.general']
    }, {
      label: 'settings.softphone.telephony_and_audio',
      icon: 'softphone',
      path: routes['settings.softphone']
    }, {
      label: 'settings.notifications.device.mobile',
      icon: 'mobile',
      path: routes['settings.mobile'],
      enabled: config.shouldShowMobile,
      settingsOnBehalf: true
    }, {
      label: 'settings.voicemail',
      icon: 'voicemail',
      path: routes['settings.voicemail'],
      settingsOnBehalf: true
    }, {
      label: "".concat(getIntegrationHost(), " ").concat(i18next.t('settings')),
      icon: 'extension',
      path: routes['settings.cti-settings'],
      enabled: config.shouldShowCti
    }, {
      label: 'settings.dashboards',
      icon: 'wallboard',
      path: routes['settings.dashboards'],
      enabled: config.shouldShowWallboard
    }, {
      label: 'settings.transcriptions',
      icon: 'article',
      path: routes['settings.transcriptions'],
      enabled: config.shouldShowTranscriptions
    }, {
      label: 'settings.could_edit',
      settingsOnBehalf: true
    }, {
      label: 'settings.notifications',
      icon: 'notifications',
      path: routes['settings.notifications'],
      showHelp: function showHelp() {
        return hasVaryingNotifications(extension.get('pushTypes'));
      },
      settingsOnBehalf: true
    }, {
      label: 'settings.callerid',
      icon: 'callerid',
      path: routes['settings.callerid'],
      showHelp: function showHelp() {
        return callerIdVaries(profiles);
      },
      settingsOnBehalf: true
    }, {
      label: 'settings.incoming_calls',
      icon: 'incoming',
      path: routes['settings.incoming-calls'],
      showHelp: function showHelp() {
        return incomingCallsVaries(profiles);
      },
      settingsOnBehalf: true
    }, {
      label: getCallRecordingTitle(profiles.first()),
      icon: 'voicemailBox',
      path: routes['settings.call-recording-and-monitoring'],
      showHelp: function showHelp() {
        return callRecordingVaries(profiles) || personalCallRecordingVaries(profiles) || monitoringVaries(profiles);
      },
      settingsOnBehalf: true
    }, {
      label: 'settings.queue_channel_login',
      icon: 'queuelogin',
      path: routes['settings.queue-and-chat-login'],
      showHelp: function showHelp() {
        return queueLoginVaries(profiles) || channelLoginVaries(profiles);
      },
      settingsOnBehalf: true
    }, {
      label: 'settings.profile_settings',
      settingsOnBehalf: true
    }, {
      label: 'profilechooser.profiles',
      icon: 'profiles',
      path: routes['settings.profiles'],
      settingsOnBehalf: true
    }, {
      label: 'settings.workhours',
      icon: 'calendar',
      path: routes['settings.work-hours'],
      settingsOnBehalf: true
    }];
  };
  return /*#__PURE__*/_jsxs("div", {
    className: "scrollbar flex size-full flex-col gap-3 overflow-y-scroll border-e p-3",
    "data-context-menu": "false",
    children: [/*#__PURE__*/_jsx(BrandLogo, {
      type: "logo24",
      className: "p-2"
    }), /*#__PURE__*/_jsx("div", {
      className: "flex-1",
      children: getNavigationForSetting(profiles, extension, config).filter(function (item) {
        return item.enabled !== false;
      }).filter(function (item) {
        return extensionKey === ownExtensionKey || item.settingsOnBehalf;
      }).map(function (item) {
        return /*#__PURE__*/_jsx(Nav, _objectSpread({}, item), item.label);
      })
    }), (!isTeamsAnyVersion(embedded) || isTeamsFullManualLogin(embedded)) && extensionKey === ownExtensionKey ? /*#__PURE__*/_jsxs("div", {
      children: [!isTeamsAnyVersion(embedded) && config.shouldShowAdminPortal && adminPortalUrl && /*#__PURE__*/_jsx(GeneralLink, {
        iconKey: "openInNew",
        href: adminPortalUrl,
        className: "bg-gray-100 px-3 hover:bg-gray-200",
        children: i18next.t('settings.to.flow')
      }), !isTeamsAnyVersion(embedded) && config.shouldShowStats && /*#__PURE__*/_jsx(GeneralLink, {
        iconKey: "openInNew",
        onClick: goToStatsInsights,
        className: "bg-gray-100 px-3 hover:bg-gray-200",
        children: i18next.t('settings.to.stats')
      }), config.shouldShowLogOut && /*#__PURE__*/_jsx(GeneralLink, {
        iconKey: "logOut",
        className: "bg-gray-100 px-3 hover:bg-gray-200",
        onClick: function onClick() {
          return logOut('Settings');
        },
        children: i18next.t('app.signout')
      })]
    }) : null]
  });
};
export default SettingsNavigation;